import React, { useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import ReactTooltip from "react-tooltip";
import styled, { css } from "styled-components";
import { MAX_TAGS_PER_USER } from "@lifeat/shared/task-management/TaskTagConstants";
import { Tag } from "@lifeat/shared/task-management/TaskTagTypes";
import {
    TagCreationError,
    canUserCreateTag,
    validateUpdateTagNameRequest,
} from "@lifeat/shared/task-management/TaskTagUtil";
import { isUserActiveProSubscriber } from "@lifeat/shared/user/UserUtil";
import { PopUpMenuComponents } from "@common/LifeAtPopupMenuV2/PopUpMenuComponents";
import { LifeAtBodySecondary } from "@common/LifeAtText/LifeAtText";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import EditPencilIcon from "@icons/EditPencilIcon.svg";
import PlusIcon from "@icons/Plus-Icon.svg";
import { FontSize } from "@themes/Fonts";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { useTaskListContext } from "../TaskListContext";
import TagCreationMenu from "./TagCreationMenu";
import TagDetailMenu from "./TagDetailMenu";

/* ------------------------------------------------------------- */
/* --------------------- Edit tag button  ---------------------- */
/* ------------------------------------------------------------- */

const StyledPencilIcon = styled(EditPencilIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

export const EditButtonPopupTrigger = styled(PopUpMenuComponents.Trigger)<{ $isVisible: boolean }>`
    display: flex;
    margin-left: auto;
    cursor: pointer;
    visibility: ${(props) => (props.$isVisible ? `visible` : `hidden`)};
    padding: 2px;
    border-radius: 3px;

    &:hover {
        background-color: ${(props) => props.theme.hoverSecondary};
    }
`;
interface EditTagButtonProps {
    tagId: string;
    tag: Tag;
}
export function EditTagButton(props: EditTagButtonProps) {
    const { tags } = useTaskListContext();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();
    const { userData } = useUserContext();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (!isUserActiveProSubscriber(userData?.proUserInfo)) {
            e.preventDefault();
            setUpgradeCtaContext("tags", UpgradeCTASource.FILTER_TAGS);
        }
    };

    return (
        <PopUpMenuComponents.Root
            open={isPopoverOpen}
            onOpenChange={(open: boolean) => setIsPopoverOpen(open)}
            modal={false}
        >
            {/* Note: for non-mobile devices, this visibility is controlled the `DropdownItem` component on hover */}
            <EditButtonPopupTrigger $isVisible={isMobile || isTablet} onClick={handleClick}>
                <StyledPencilIcon />
            </EditButtonPopupTrigger>
            <PopUpMenuComponents.Content>
                <TagDetailMenu
                    tagId={props.tagId}
                    tag={props.tag}
                    validateTagName={(tagName: string) =>
                        validateUpdateTagNameRequest(
                            tagName,
                            Object.values(tags).map((item) => item.document),
                        )
                    }
                    handleClosePopup={() => setIsPopoverOpen(false)}
                />
            </PopUpMenuComponents.Content>
        </PopUpMenuComponents.Root>
    );
}

/* ------------------------------------------------------------- */
/* -------------------- Dropdown item div ---------------------- */
/* ------------------------------------------------------------- */
const DropdownItemContainer = styled.div<{ isDisabled?: boolean; blockCursor?: boolean }>`
    display: flex;
    width: 100%;
    align-items: center;

    color: ${(props) => (props.isDisabled ? props.theme.textColorSecondary : props.theme.textColorPrimary)};
    opacity: ${(props) => (props.isDisabled ? `0.7` : `1`)};

    font-size: ${FontSize.normal};
    padding: 10px;

    cursor: pointer;

    ${(props) =>
        props.blockCursor &&
        css`
            cursor: not-allowed;
            * {
                cursor: not-allowed;
            }
        `};

    &:hover {
        background-color: ${(props) => props.theme.hoverPrimary};
    }

    &:hover ${EditButtonPopupTrigger} {
        visibility: visible;
    }
`;

export function DropdownItem(props: {
    onClick: () => void;
    isDisabled?: boolean;
    blockCursor?: boolean;
    children: React.ReactNode;
}) {
    return (
        <DropdownItemContainer
            isDisabled={props.isDisabled}
            blockCursor={props.blockCursor}
            onClick={(e) => {
                e.stopPropagation();
                props.onClick();
            }}
        >
            {props.children}
        </DropdownItemContainer>
    );
}

/* ------------------------------------------------------------- */
/* ------------------- Default tag indicator ------------------- */
/* ------------------------------------------------------------- */

const DEFAULT_TAG_LABEL = "(default)";

const DefaultTagIndicatorText = styled(LifeAtBodySecondary)`
    padding: 0 4px;
`;

export function DefaultTagIndicator() {
    return <DefaultTagIndicatorText>{DEFAULT_TAG_LABEL}</DefaultTagIndicatorText>;
}

/* ------------------------------------------------------------- */
/* ----------------- Add new tag dropdown item ----------------- */
/* ------------------------------------------------------------- */

const CREATE_TAG_LABEL = "Create new tag";
const DISABLE_CREATE_TAG_TOOLTIP = `It seems like you're on a roll and have reached the ${MAX_TAGS_PER_USER} tags limit! Try deleting a tag`;

const AddButtonPopupTrigger = styled(PopUpMenuComponents.Trigger)`
    width: 100%;
    cursor: pointer;
`;

const ButtonContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const StyledPlusIcon = styled(PlusIcon)`
    stroke: ${(props) => props.theme.iconColor};
`;

export function AddNewTagDropdownItem() {
    const { tags } = useTaskListContext();
    const { userData } = useUserContext();
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const isProUser = isUserActiveProSubscriber(userData?.proUserInfo);

    const validateTagName = (tagName: string) => {
        return validateUpdateTagNameRequest(
            tagName,
            Object.values(tags).map((item) => item.document),
        );
    };

    const canUserCreateTagResponse = canUserCreateTag(isProUser, Object.values(tags));

    const handleAddNewTagClick: React.MouseEventHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (canUserCreateTagResponse.isValid === false) {
            e.preventDefault();
            if (canUserCreateTagResponse.reason === TagCreationError.FreeUser) {
                setUpgradeCtaContext("tags", UpgradeCTASource.FILTER_TAGS);
            }
        }
    };

    const createTagButtonContent = (
        <>
            <span>{CREATE_TAG_LABEL}</span>
            <StyledPlusIcon />
        </>
    );

    return (
        <PopUpMenuComponents.Root open={isPopoverOpen} onOpenChange={(open: boolean) => setIsPopoverOpen(open)}>
            <AddButtonPopupTrigger onClick={handleAddNewTagClick}>
                <DropdownItemContainer
                    isDisabled={!canUserCreateTagResponse.isValid && isProUser}
                    blockCursor={
                        canUserCreateTagResponse.isValid === false &&
                        canUserCreateTagResponse.reason === TagCreationError.MaxTagsPerUser
                    }
                    data-tip={DISABLE_CREATE_TAG_TOOLTIP}
                    data-for={`tag-limit-reached`}
                >
                    <ButtonContent>{createTagButtonContent}</ButtonContent>
                    {canUserCreateTagResponse.isValid === false &&
                        canUserCreateTagResponse.reason === TagCreationError.MaxTagsPerUser && (
                            <ReactTooltip id={`tag-limit-reached`} />
                        )}
                </DropdownItemContainer>
            </AddButtonPopupTrigger>
            <PopUpMenuComponents.Content>
                <TagCreationMenu validateTagName={validateTagName} handleClosePopup={() => setIsPopoverOpen(false)} />
            </PopUpMenuComponents.Content>
        </PopUpMenuComponents.Root>
    );
}
